import type { Bitmap, RGB } from "./bitmap";

/** A 5×7 pixel font for signs, posters and box covers inside the world. */
const G: Record<string, string> = {
  A: "01110 10001 10001 11111 10001 10001 10001",
  B: "11110 10001 10001 11110 10001 10001 11110",
  C: "01110 10001 10000 10000 10000 10001 01110",
  D: "11100 10010 10001 10001 10001 10010 11100",
  E: "11111 10000 10000 11110 10000 10000 11111",
  F: "11111 10000 10000 11110 10000 10000 10000",
  G: "01110 10001 10000 10111 10001 10001 01111",
  H: "10001 10001 10001 11111 10001 10001 10001",
  I: "01110 00100 00100 00100 00100 00100 01110",
  J: "00111 00010 00010 00010 00010 10010 01100",
  K: "10001 10010 10100 11000 10100 10010 10001",
  L: "10000 10000 10000 10000 10000 10000 11111",
  M: "10001 11011 10101 10101 10001 10001 10001",
  N: "10001 10001 11001 10101 10011 10001 10001",
  O: "01110 10001 10001 10001 10001 10001 01110",
  P: "11110 10001 10001 11110 10000 10000 10000",
  Q: "01110 10001 10001 10001 10101 10010 01101",
  R: "11110 10001 10001 11110 10100 10010 10001",
  S: "01111 10000 10000 01110 00001 00001 11110",
  T: "11111 00100 00100 00100 00100 00100 00100",
  U: "10001 10001 10001 10001 10001 10001 01110",
  V: "10001 10001 10001 10001 10001 01010 00100",
  W: "10001 10001 10001 10101 10101 10101 01010",
  X: "10001 10001 01010 00100 01010 10001 10001",
  Y: "10001 10001 01010 00100 00100 00100 00100",
  Z: "11111 00001 00010 00100 01000 10000 11111",
  "0": "01110 10001 10011 10101 11001 10001 01110",
  "1": "00100 01100 00100 00100 00100 00100 01110",
  "2": "01110 10001 00001 00010 00100 01000 11111",
  "3": "11111 00010 00100 00010 00001 10001 01110",
  "4": "00010 00110 01010 10010 11111 00010 00010",
  "5": "11111 10000 11110 00001 00001 10001 01110",
  "6": "00110 01000 10000 11110 10001 10001 01110",
  "7": "11111 00001 00010 00100 01000 01000 01000",
  "8": "01110 10001 10001 01110 10001 10001 01110",
  "9": "01110 10001 10001 01111 00001 00010 01100",
  "&": "01100 10010 10100 01000 10101 10010 01101",
  "!": "00100 00100 00100 00100 00100 00000 00100",
  "?": "01110 10001 00001 00010 00100 00000 00100",
  "'": "00100 00100 01000 00000 00000 00000 00000",
  "-": "00000 00000 00000 11111 00000 00000 00000",
  ".": "00000 00000 00000 00000 00000 01100 01100",
  ",": "00000 00000 00000 00000 01100 00100 01000",
  ":": "00000 01100 01100 00000 01100 01100 00000",
  "$": "00100 01111 10100 01110 00101 11110 00100",
  " ": "00000 00000 00000 00000 00000 00000 00000",
};

const glyphs: Record<string, boolean[][]> = {};
for (const [k, v] of Object.entries(G)) glyphs[k] = v.split(" ").map((row) => [...row].map((c) => c === "1"));

export const FONT_W = 5;
export const FONT_H = 7;

export function textWidth(s: string, gap = 1): number {
  return s.length ? s.length * (FONT_W + gap) - gap : 0;
}

/** Draws upper-case text; unknown characters are skipped as spaces. */
export function drawText(b: Bitmap, x: number, y: number, s: string, c: RGB, gap = 1, scale = 1): void {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) {
    const g = glyphs[ch] ?? glyphs[" "];
    for (let r = 0; r < FONT_H; r++) for (let col = 0; col < FONT_W; col++) if (g[r][col]) b.rect(cx + col * scale, y + r * scale, scale, scale, c);
    cx += (FONT_W + gap) * scale;
  }
}

export function hasGlyph(ch: string): boolean {
  return ch.toUpperCase() in glyphs;
}

/** A 3×5 pixel font for long box titles and subtitles, where the 5×7 one would not fit. */
const S: Record<string, string> = {
  A: "010 101 111 101 101", B: "110 101 110 101 110", C: "011 100 100 100 011", D: "110 101 101 101 110",
  E: "111 100 110 100 111", F: "111 100 110 100 100", G: "011 100 101 101 011", H: "101 101 111 101 101",
  I: "111 010 010 010 111", J: "001 001 001 101 010", K: "101 101 110 101 101", L: "100 100 100 100 111",
  M: "101 111 111 101 101", N: "110 101 101 101 101", O: "010 101 101 101 010", P: "110 101 110 100 100",
  Q: "010 101 101 110 011", R: "110 101 110 101 101", S: "011 100 010 001 110", T: "111 010 010 010 010",
  U: "101 101 101 101 111", V: "101 101 101 101 010", W: "101 101 111 111 101", X: "101 101 010 101 101",
  Y: "101 101 010 010 010", Z: "111 001 010 100 111",
  "0": "111 101 101 101 111", "1": "010 110 010 010 111", "2": "110 001 010 100 111", "3": "110 001 010 001 110",
  "4": "101 101 111 001 001", "5": "111 100 110 001 110", "6": "011 100 111 101 111", "7": "111 001 010 010 010",
  "8": "111 101 111 101 111", "9": "111 101 111 001 110",
  "&": "010 101 010 101 011", "!": "010 010 010 000 010", "?": "110 001 010 000 010", "'": "010 010 000 000 000",
  "-": "000 000 111 000 000", ".": "000 000 000 000 010", ",": "000 000 000 010 100", ":": "000 010 000 010 000",
  "$": "011 110 010 011 110", " ": "000 000 000 000 000",
};
const small: Record<string, boolean[][]> = {};
for (const [k, v] of Object.entries(S)) small[k] = v.split(" ").map((row) => [...row].map((c) => c === "1"));

export const SMALL_W = 3;
export const SMALL_H = 5;

export function smallTextWidth(s: string, gap = 1): number {
  return s.length ? s.length * (SMALL_W + gap) - gap : 0;
}

/** Draws upper-case text in the 3×5 font; unknown characters are skipped as spaces. */
export function drawSmallText(b: Bitmap, x: number, y: number, s: string, c: RGB, gap = 1): void {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) {
    const g = small[ch] ?? small[" "];
    for (let r = 0; r < SMALL_H; r++) for (let col = 0; col < SMALL_W; col++) if (g[r][col]) b.set(cx + col, y + r, c);
    cx += SMALL_W + gap;
  }
}
